/**
 * BrowserPhoneSIP.js - Direct implementation based on Browser-Phone working code
 * Follows the exact same pattern as Browser-Phone phone.js for guaranteed compatibility
 */

// Global variables to match Browser-Phone pattern
let userAgent = null;
let sipConfig = null;
let incomingSessions = new Map(); // Track incoming sessions by callId
let outgoingSessions = new Map(); // Track outgoing sessions by callId
let currentSession = null; // Track the current active session
let activeSessions = new Map(); // Track all active sessions for multi-call support
let localStream = null;
let remoteStream = null;
let holdMusicAudio = null; // Hold music audio element
let audioCheckInterval = null; // Interval to check audio health

// Browser-Phone default values
const appversion = "0.3.26";
const sipjsversion = "0.20.0";
const navUserAgent = window.navigator.userAgent;

// Default configuration values (matching Browser-Phone defaults)
let TransportConnectionTimeout = 15;
let TransportReconnectionAttempts = 999;
let TransportReconnectionTimeout = 3;
let RegisterExpires = 300;
let ContactUserName = "";
let IpInContact = true; // Important for Asterisk
let BundlePolicy = "balanced";
let IceStunCheckTimeout = 500;
let NoAnswerTimeout = 120;
let WssInTransport = true; // Required for Asterisk

// Event callbacks
let eventCallbacks = {};

// Register event callback
function on(event, callback) {
  if (!eventCallbacks[event]) {
    eventCallbacks[event] = [];
  }
  eventCallbacks[event].push(callback);
}

// Emit event
function emit(event, data) {
  if (eventCallbacks[event]) {
    eventCallbacks[event].forEach(callback => callback(data));
  }
}

// Create User Agent - EXACTLY like Browser-Phone CreateUserAgent function
function CreateUserAgent(config) {
  // console.log("Creating User Agent...");

  // Store config globally
  sipConfig = config;

  // Extract values from config
  const SipUsername = config.username;
  const SipPassword = config.password;
  const domainParts = config.domain.split(':');
  const wssServer = domainParts[0];
  const WebSocketPort = domainParts[1] || '8089';
  const ServerPath = '/ws';

  // SIP Domain logic EXACTLY like Browser-Phone line 1929
  let SipDomain = config.domain;
  if (SipDomain == null || SipDomain == "" || SipDomain == "null" || SipDomain == "undefined") {
    SipDomain = wssServer;
  } else {
    // Use just the server part for SIP domain
    SipDomain = wssServer;
  }

  const profileName = SipUsername; // Use username as profile name
  const userAgentStr = `Browser Phone ${appversion} (SIPJS - ${sipjsversion}) ${navUserAgent}`;

  // console.log('SIP Configuration:');
  // console.log('  - SIP Username:', SipUsername);
  // console.log('  - SIP Domain:', SipDomain);
  // console.log('  - WSS Server:', wssServer);
  // console.log('  - WebSocket Port:', WebSocketPort);
  // console.log('  - Server Path:', ServerPath);
  // console.log('  - Full WebSocket URL:', `wss://${wssServer}:${WebSocketPort}${ServerPath}`);

  // UserAgent options - EXACTLY matching Browser-Phone options structure
  var options = {
    logConfiguration: false,
    uri: window.SIP.UserAgent.makeURI("sip:" + SipUsername + "@" + SipDomain),
    transportOptions: {
      server: "wss://" + wssServer + ":" + WebSocketPort + "" + ServerPath,
      traceSip: false,
      connectionTimeout: TransportConnectionTimeout,
      keepAliveInterval: 30 // Send keep-alive every 30 seconds to prevent idle disconnect
    },
    sessionDescriptionHandlerFactoryOptions: {
      peerConnectionConfiguration: {
        bundlePolicy: BundlePolicy
        // iceServers will be set below if not specified
      },
      iceGatheringTimeout: IceStunCheckTimeout
    },
    contactName: ContactUserName,
    displayName: profileName,
    authorizationUsername: SipUsername,
    authorizationPassword: SipPassword,
    hackIpInContact: IpInContact, // Important for Asterisk
    userAgentString: userAgentStr,
    autoStart: false,
    autoStop: true,
    register: false,
    noAnswerTimeout: NoAnswerTimeout,
    contactParams: {},
    delegate: {
      onInvite: function (sip) {
        ReceiveCall(sip);
      },
      onMessage: function (sip) {
        ReceiveOutOfDialogMessage(sip);
      }
    }
  };

  // Set default ICE servers (matching Browser-Phone default)
  if (!options.sessionDescriptionHandlerFactoryOptions.peerConnectionConfiguration.iceServers) {
    options.sessionDescriptionHandlerFactoryOptions.peerConnectionConfiguration.iceServers = [
      { urls: "stun:stun.l.google.com:19302" }
    ];
  }

  // Set transport in contactParams (Browser-Phone pattern)
  if (WssInTransport) {
    try {
      options.contactParams.transport = "wss";
    } catch (e) {
      console.warn('Failed to set transport in contactParams:', e);
    }
  }

  // console.log('Final UserAgent options:', options);

  // Create UserAgent - EXACTLY like Browser-Phone
  userAgent = new window.SIP.UserAgent(options);

  // Add Browser-Phone custom properties
  userAgent.isRegistered = function() {
    return (userAgent && userAgent.registerer && userAgent.registerer.state == window.SIP.RegistererState.Registered);
  };

  // For some reason this is marked as private... not sure why (Browser-Phone comment)
  userAgent.sessions = userAgent._sessions;
  userAgent.registrationCompleted = false;
  userAgent.registering = false;
  userAgent.transport.ReconnectionAttempts = TransportReconnectionAttempts;
  userAgent.transport.attemptingReconnection = false;
  userAgent.BlfSubs = [];
  userAgent.lastVoicemailCount = 0;

  // console.log("Creating User Agent... Done");

  // Transport event handlers - EXACTLY like Browser-Phone
  userAgent.transport.onConnect = function() {
    onTransportConnected();
  };

  userAgent.transport.onDisconnect = function(error) {
    if (error) {
      onTransportConnectError(error);
    } else {
      onTransportDisconnected();
    }
  };

  // Create Registerer - EXACTLY like Browser-Phone
  var RegistererOptions = {
    logConfiguration: false,
    expires: RegisterExpires,
    extraHeaders: [],
    extraContactHeaderParams: [],
    refreshFrequency: 75 // Re-register at 75% of expiration time
  };

  userAgent.registerer = new window.SIP.Registerer(userAgent, RegistererOptions);
  // console.log("Creating Registerer... Done");

  // Registration state listener - EXACTLY like Browser-Phone
  userAgent.registerer.stateChange.addListener(function(newState) {
    // console.log("User Agent Registration State:", newState);
    switch (newState) {
      case window.SIP.RegistererState.Initial:
        // Nothing to do
        break;
      case window.SIP.RegistererState.Registered:
        onRegistered();
        break;
      case window.SIP.RegistererState.Unregistered:
        onUnregistered();
        break;
      case window.SIP.RegistererState.Terminated:
        onRegistrationTerminated();
        break;
    }
  });

  // Start UserAgent - EXACTLY like Browser-Phone
  // console.log("User Agent Connecting to WebSocket...");
  userAgent.start().catch(function(error) {
    onTransportConnectError(error);
  });
}

// Transport Events - EXACTLY like Browser-Phone
function onTransportConnected() {
  // console.log("Connected to Web Socket!");

  // Check if userAgent exists
  if (!userAgent) {
    console.warn("onTransportConnected: userAgent is null, skipping registration");
    return;
  }

  // Reset the ReconnectionAttempts (Browser-Phone logic)
  userAgent.isReRegister = false;
  userAgent.transport.attemptingReconnection = false;
  userAgent.transport.ReconnectionAttempts = TransportReconnectionAttempts;

  // Auto start register (Browser-Phone logic)
  if (userAgent.transport.attemptingReconnection == false && userAgent.registering == false) {
    window.setTimeout(function() {
      Register();
    }, 500);
  } else {
    console.warn("onTransportConnected: Register() called, but attemptingReconnection is true or registering is true");
  }
}

function onTransportConnectError(error) {
  console.warn("WebSocket Connection Failed:", error);

  // Check if userAgent exists before accessing properties
  if (!userAgent) {
    console.warn("onTransportConnectError: userAgent is null, skipping operations");
    emit('error', error);
    return;
  }

  // We set this flag here so that the re-register attempts are fully completed. (Browser-Phone comment)
  userAgent.isReRegister = false;

  // If there is an issue with the WS connection (Browser-Phone comment)
  // We unregister, so that we register again once its up (Browser-Phone comment)
  // console.log("Unregister...");
  try {
    if (userAgent.registerer) {
      userAgent.registerer.unregister();
    }
  } catch (e) {
    // I know!!! (Browser-Phone comment)
  }

  emit('error', error);
}

function onTransportDisconnected() {
  // console.log("Disconnected from Web Socket!");

  // Check if userAgent exists before accessing properties
  if (userAgent) {
    userAgent.isReRegister = false;
  }

  emit('disconnected');
}

// Registration - EXACTLY like Browser-Phone Register function
function Register() {
  if (userAgent == null) return;
  if (userAgent.registering == true) return;
  if (userAgent.isRegistered()) return;

  var RegistererRegisterOptions = {
    requestDelegate: {
      onReject: function(sip) {
        onRegisterFailed(sip.message.reasonPhrase, sip.message.statusCode);
      }
    }
  };

  // console.log("Sending Registration...");
  userAgent.registering = true;
  userAgent.registerer.register(RegistererRegisterOptions);
}

// Registration Events - EXACTLY like Browser-Phone
function onRegistered() {
  // This code fires on re-register after session timeout (Browser-Phone comment)
  // to ensure that events are not fired multiple times (Browser-Phone comment)
  // a isReRegister state is kept. (Browser-Phone comment)

  userAgent.registrationCompleted = true;
  if (!userAgent.isReRegister) {
    // console.log("Registered!");

    userAgent.registering = false;
    emit('registered');
  } else {
    userAgent.registering = false;
    // console.log("ReRegistered!");
  }
  userAgent.isReRegister = true;
}

function onRegisterFailed(response, cause) {
  // console.log("Registration Failed: " + response);

  userAgent.registering = false;
  emit('registrationFailed', { reasonPhrase: response, statusCode: cause });
}

function onUnregistered() {
  if (userAgent.registrationCompleted) {
    // console.log("Unregistered, bye!");
    emit('unregistered');
  } else {
    // Was never really registered, so cant really say unregistered (Browser-Phone comment)
  }

  // We set this flag here so that the re-register attempts are fully completed. (Browser-Phone comment)
  userAgent.isReRegister = false;
}

function onRegistrationTerminated() {
  // console.log("Registration terminated");
  userAgent.registering = false;
  emit('unregistered');
}

// Incoming call handler - properly store session for answer
function ReceiveCall(session) {
  const callId = session.id || Date.now().toString();
  const callerNumber = session.remoteIdentity?.uri?.user || 'Unknown';
  console.log("📞 [ASTERISK] Incoming call RINGING from:", callerNumber, "- CallID:", callId);

  // Store the session for later use during answer
  incomingSessions.set(callId, session);

  // CRITICAL: Setup media handlers BEFORE answer
  session.delegate = {
    onSessionDescriptionHandler: (sdh, provisional) => {
      // console.log('🎵 Session description handler created for incoming call');
      setupSessionDescriptionHandler(session, sdh);
    },
    onBye: (sip) => {
      console.log('📞 [ASTERISK] Incoming call ended by REMOTE PARTY - CallID:', callId);
      // Note: callEnded event will be emitted by stateChange Terminated listener to avoid duplicates
      // Cleanup is also handled there
    }
  };

  // Setup session state handlers
  session.stateChange.addListener((newState) => {
    console.log('📞 [ASTERISK] Incoming call state changed:', newState, 'CallID:', callId);
    if (window.SIP.SessionState) {
      switch (newState) {
        case window.SIP.SessionState.Established:
          console.log('✅ [ASTERISK] Incoming call CONNECTED - CallID:', callId);
          // Add to active sessions when established
          activeSessions.set(callId, session);

          // If we already have an active call, put it on hold
          if (currentSession && currentSession !== session) {
            console.log('📞 [ASTERISK] Putting current call on hold for incoming call');
            holdSession(currentSession, true).catch(err => console.warn('Failed to hold current call:', err));
          }

          // Media handlers should already be set up via delegate
          currentSession = session;
          emit('callAccepted', { callId });
          break;
        case window.SIP.SessionState.Terminated:
          console.log('📞 [ASTERISK] Incoming call ENDED/TERMINATED - CallID:', callId);
          incomingSessions.delete(callId);
          activeSessions.delete(callId);

          if (currentSession === session) {
            const remainingSessions = Array.from(activeSessions.values());
            currentSession = remainingSessions.length > 0 ? remainingSessions[0] : null;

            if (currentSession && currentSession.isOnHold) {
              // Resume the remaining call
              hold(false).catch(err => console.warn('Failed to resume remaining call:', err));
            }
          }

          if (activeSessions.size === 0) {
            cleanupMedia();
          }

          emit('callEnded', { callId });
          break;
      }
    }
  });

  emit('invite', {
    callId: callId,
    session: session
  });
}

// Outbound message handler - simplified for now
function ReceiveOutOfDialogMessage(sip) {
  // console.log("Received out-of-dialog message");
}

// Get user media for calls
async function getUserMedia() {
  if (!localStream) {
    try {
      localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        },
        video: false
      });
      // console.log('✅ Got user media stream:', localStream);
    } catch (error) {
      // console.error('❌ Failed to get user media:', error);
      throw error;
    }
  }
  return localStream;
}

// Setup audio elements for remote stream - Browser-Phone pattern
function setupRemoteAudio(stream) {
  // console.log('🎵 Setting up remote audio element (Browser-Phone pattern)');
  // console.log('🎵 Stream tracks:', stream.getTracks().length);

  // Log track details
  stream.getTracks().forEach(track => {
    // console.log(`🎵 Stream track: ${track.kind}, enabled: ${track.enabled}, muted: ${track.muted}, readyState: ${track.readyState}`);
  });

  // Find or create the remote audio element
  let remoteAudio = document.querySelector('#line-remoteAudio');
  if (!remoteAudio) {
    // console.log('🎵 Creating new remote audio element');
    remoteAudio = document.createElement('audio');
    remoteAudio.id = 'line-remoteAudio';
    remoteAudio.style.display = 'none';
    remoteAudio.autoplay = true;
    remoteAudio.playsInline = true;
    document.body.appendChild(remoteAudio);
  }

  // ALWAYS update the stream, even if element exists
  // console.log('🎵 Setting srcObject on remote audio element');
  remoteAudio.srcObject = stream;

  // Force play immediately if possible
  const attemptPlay = () => {
    const playPromise = remoteAudio.play();
    if (playPromise !== undefined) {
      playPromise.then(() => {
        // console.log('✅ Remote audio playing successfully (Browser-Phone pattern)');
      }).catch(error => {
        console.warn('⚠️ Autoplay prevented, setting up user interaction handler:', error);
        // Browser-Phone pattern: try to play on user interaction
        const playAudioOnClick = () => {
          // console.log('🎵 User interaction detected, trying to play audio');
          remoteAudio.play().then(() => {
            // console.log('✅ Remote audio started after user interaction');
            document.removeEventListener('click', playAudioOnClick);
            document.removeEventListener('touchstart', playAudioOnClick);
          }).catch(err => {
            // console.error('❌ Still failed to play after user interaction:', err);
          });
        };
        document.addEventListener('click', playAudioOnClick, { once: true });
        document.addEventListener('touchstart', playAudioOnClick, { once: true });
      });
    }
  };

  // Setup onloadedmetadata handler - exact Browser-Phone pattern
  remoteAudio.onloadedmetadata = function(e) {
    // console.log('🎵 Remote audio metadata loaded, attempting play');

    // Handle audio output device selection (Browser-Phone has setSinkId)
    if (typeof remoteAudio.sinkId !== 'undefined') {
      // For now, use default output - Browser-Phone calls getAudioOutputID()
      // console.log('🎵 Audio sink available but using default output');
    }

    attemptPlay();
  };

  // Also try to play immediately in case metadata is already loaded
  if (remoteAudio.readyState >= 1) { // HAVE_METADATA or higher
    // console.log('🎵 Audio metadata already loaded, attempting immediate play');
    attemptPlay();
  }

  // console.log('✅ Remote audio element setup complete (Browser-Phone pattern)');

  // Start periodic audio health check
  startAudioHealthCheck();

  return remoteAudio;
}

// Start periodic audio health monitoring
function startAudioHealthCheck() {
  // Clear any existing interval
  if (audioCheckInterval) {
    clearInterval(audioCheckInterval);
  }

  audioCheckInterval = setInterval(() => {
    if (currentSession && currentSession.state === window.SIP.SessionState.Established && !currentSession.isOnHold) {
      const remoteAudio = document.querySelector('#line-remoteAudio');

      if (remoteAudio) {
        // Check if audio element has a stream and is playing
        if (!remoteAudio.srcObject || remoteAudio.paused) {
          // console.log('🎵 Audio health check: Remote audio not playing, attempting fix');

          // Try to re-establish remote audio
          if (currentSession.sessionDescriptionHandler?.peerConnection) {
            onTrackAddedEvent(currentSession);
          }
        } else {
          // Check if stream has active tracks
          const stream = remoteAudio.srcObject;
          const audioTracks = stream.getAudioTracks();
          const liveAudioTracks = audioTracks.filter(track => track.readyState === 'live' && track.enabled);

          if (liveAudioTracks.length === 0) {
            // console.log('🎵 Audio health check: No live audio tracks, attempting fix');
            onTrackAddedEvent(currentSession);
          }
        }
      } else {
        // console.log('🎵 Audio health check: No remote audio element, attempting fix');
        if (currentSession.sessionDescriptionHandler?.peerConnection) {
          onTrackAddedEvent(currentSession);
        }
      }
    }
  }, 3000); // Check every 3 seconds
}

// Stop audio health monitoring
function stopAudioHealthCheck() {
  if (audioCheckInterval) {
    clearInterval(audioCheckInterval);
    audioCheckInterval = null;
    // console.log('🎵 Audio health monitoring stopped');
  }
}

// Make outgoing call - enhanced with multi-call support
async function makeCall(numberToCall, isConsultationCall = false) {
  if (!userAgent || !userAgent.isRegistered()) {
    throw new Error('SIP extension not registered');
  }

  try {
    // For consultation calls, we don't need fresh media - reuse existing
    if (!isConsultationCall || !localStream) {
      await getUserMedia();
    }

    // Convert number to string if it's not already
    const number = typeof numberToCall === 'string' ? numberToCall : String(numberToCall);
    // console.log(`Making SIP call to: ${number} (consultation: ${isConsultationCall})`);

    // Check if sipConfig exists
    if (!sipConfig || !sipConfig.domain) {
      throw new Error('SIP configuration not available. Make sure connect() was called first.');
    }

    // Use the same domain logic as registration
    const domainParts = sipConfig.domain.split(':');
    const SipDomain = domainParts[0];

    const sipUri = `sip:${number.replace(/#/g, '%23')}@${SipDomain}`;
    const targetURI = window.SIP.UserAgent.makeURI(sipUri);

    const inviteOptions = {
      earlyMedia: true,
      sessionDescriptionHandlerOptions: {
        constraints: {
          audio: true,
          video: false
        }
      }
    };

    // Create Inviter (outgoing call)
    const inviter = new window.SIP.Inviter(userAgent, targetURI, inviteOptions);
    const callId = inviter.id || Date.now().toString();

    // Store the called number for later use
    inviter.calledNumber = number;

    // Store the session
    outgoingSessions.set(callId, inviter);
    activeSessions.set(callId, inviter);

    // If this is a consultation call and we have an existing call, put the existing call on hold
    if (isConsultationCall && currentSession && currentSession !== inviter) {
      // console.log('📞 Putting current call on hold for consultation call');
      try {
        await hold(true); // Hold the current call
      } catch (error) {
        console.warn('⚠️ Failed to hold current call before consultation:', error);
      }
    }

    // Set new call as current session
    currentSession = inviter;

    // CRITICAL: Setup media handlers BEFORE sending invite
    inviter.delegate = {
      onSessionDescriptionHandler: (sdh, provisional) => {
        // console.log('🎵 Session description handler created for outbound call');
        setupSessionDescriptionHandler(inviter, sdh);
      }
    };

    // Setup session delegates and state handlers
    inviter.delegate.onBye = (sip) => {
      console.log('📞 [ASTERISK] Outbound call ended by REMOTE PARTY - CallID:', callId);
      // Note: callEnded event will be emitted by stateChange Terminated listener to avoid duplicates
      // Cleanup is also handled there
    };

    inviter.stateChange.addListener((newState) => {
      console.log('📞 [ASTERISK] Outbound call state changed:', newState, 'CallID:', callId);
      if (window.SIP.SessionState) {
        switch (newState) {
          case window.SIP.SessionState.Initial:
            console.log('📞 [ASTERISK] Outbound call INITIATING - CallID:', callId);
            emit('callInitiating', { callId });
            break;
          case window.SIP.SessionState.Establishing:
            console.log('📞 [ASTERISK] Outbound call RINGING/ESTABLISHING - CallID:', callId);
            emit('callConnecting', { callId }); // This should trigger ringing state
            break;
          case window.SIP.SessionState.Established:
            console.log('✅ [ASTERISK] Outbound call CONNECTED - CallID:', callId);
            // Media handlers should already be set up via delegate
            emit('callAccepted', { callId });
            break;
          case window.SIP.SessionState.Terminated:
            console.log('📞 [ASTERISK] Outbound call ENDED/TERMINATED - CallID:', callId);
            outgoingSessions.delete(callId);
            activeSessions.delete(callId);

            // If this was the current session, switch to another active session or clear
            if (currentSession === inviter) {
              const remainingSessions = Array.from(activeSessions.values());
              currentSession = remainingSessions.length > 0 ? remainingSessions[0] : null;

              if (currentSession) {
                // console.log('📞 Switching to remaining session after termination');
                // Resume the other call if it was on hold
                if (currentSession.isOnHold) {
                  hold(false).catch(err => console.warn('Failed to resume other call:', err));
                }
              }
            }

            if (activeSessions.size === 0) {
              cleanupMedia();
            }
            emit('callEnded', { callId });
            break;
        }
      }
    });

    // Send INVITE with proper request delegates
    const inviterOptions = {
      requestDelegate: {
        onTrying: (sip) => {
          console.log('📞 [ASTERISK] Call TRYING (100 Trying) - CallID:', callId);
          emit('callConnecting', { callId });
        },
        onProgress: (sip) => {
          console.log('📞 [ASTERISK] Call RINGING (180/183 Progress) - CallID:', callId);
          emit('callConnecting', { callId });
        },
        onAccept: (sip) => {
          console.log('✅ [ASTERISK] Call ACCEPTED (200 OK) - CallID:', callId);
          // Media should already be setup via delegate
          // Note: callAccepted event is emitted by stateChange listener, not here to avoid duplicates
        },
        onReject: (sip) => {
          const statusCode = sip.message.statusCode;
          const reasonPhrase = sip.message.reasonPhrase;
          const calledNumber = inviter.calledNumber;

          console.log('❌ [ASTERISK] Call REJECTED:', reasonPhrase, 'Status Code:', statusCode, '- CallID:', callId);

          // Determine error code based on SIP status code
          let errorCode = 'busy'; // default
          if (statusCode === 404 || statusCode === 410 || statusCode === 604) {
            errorCode = 'notavailable'; // Phone number not available
          } else if (statusCode === 486 || statusCode === 600 || statusCode === 603 || statusCode === 480) {
            errorCode = 'busy'; // Customer rejected/busy/blocked
          }

          console.log('🔔 [ASTERISK] Error Code for rejection:', errorCode);

          outgoingSessions.delete(callId);
          activeSessions.delete(callId);

          // If this was a consultation call and it failed, resume the original call
          if (isConsultationCall) {
            const remainingSessions = Array.from(activeSessions.values());
            if (remainingSessions.length > 0) {
              currentSession = remainingSessions[0];
              if (currentSession.isOnHold) {
                hold(false).catch(err => console.warn('Failed to resume original call:', err));
              }
            }
          } else {
            currentSession = null;
          }

          if (activeSessions.size === 0) {
            cleanupMedia();
          }
          emit('callFailed', {
            callId,
            reasonPhrase,
            statusCode,
            errorCode,
            calledNumber
          });
        }
      }
    };

    // Wait for call to be accepted or at least trying/progress before returning
    await new Promise((resolve, reject) => {
      let resolved = false;

      const originalRequestDelegate = inviterOptions.requestDelegate;
      inviterOptions.requestDelegate = {
        ...originalRequestDelegate,
        onTrying: (sip) => {
          if (!resolved) {
            resolved = true;
            resolve(); // Call is at least being processed by server
          }
          originalRequestDelegate.onTrying?.(sip);
        },
        onProgress: (sip) => {
          if (!resolved) {
            resolved = true;
            resolve(); // Call is progressing (ringing)
          }
          originalRequestDelegate.onProgress?.(sip);
        },
        onAccept: (sip) => {
          if (!resolved) {
            resolved = true;
            resolve(); // Call accepted
          }
          originalRequestDelegate.onAccept?.(sip);
        },
        onReject: (sip) => {
          if (!resolved) {
            resolved = true;
            reject(new Error(`Call rejected: ${sip.message.reasonPhrase || 'Unknown reason'}`));
          }
          originalRequestDelegate.onReject?.(sip);
        }
      };

      // Set a timeout in case the server doesn't respond
      setTimeout(() => {
        if (!resolved) {
          resolved = true;
          reject(new Error('Call setup timeout - no response from server'));
        }
      }, 10000); // 10 second timeout

      inviter.invite(inviterOptions).catch(error => {
        if (!resolved) {
          resolved = true;
          reject(error);
        }
      });
    });

    return {
      id: callId,
      session: inviter
    };

  } catch (error) {
    // console.error('❌ Failed to make call:', error);
    if (activeSessions.size === 0) {
      cleanupMedia();
    }
    // Include callId in error object for proper tracking
    const errorWithId = {
      ...error,
      callId: callId,
      sessionId: callId,
      message: error.message,
      reasonPhrase: error.reasonPhrase || error.message
    };
    emit('callFailed', errorWithId);
    throw error;
  }
}

// Browser-Phone onTrackAddedEvent equivalent
function onTrackAddedEvent(session) {
  // console.log('🎵 onTrackAddedEvent - Browser-Phone pattern');

  // Get peer connection - Browser-Phone pattern
  const pc = session.sessionDescriptionHandler?.peerConnection;
  if (!pc) {
    console.warn('⚠️ No peer connection available');
    return;
  }

  // Create remote streams - exact Browser-Phone pattern
  const remoteAudioStream = new MediaStream();
  const remoteVideoStream = new MediaStream();

  let audioTracksAdded = 0;

  // Process transceivers - exact Browser-Phone pattern
  pc.getTransceivers().forEach(function (transceiver) {
    const receiver = transceiver.receiver;
    if (receiver.track && receiver.track.readyState === 'live') {
      if (receiver.track.kind === "audio") {
        // console.log("🎵 Adding Remote Audio Track (Browser-Phone pattern):", receiver.track.id);
        remoteAudioStream.addTrack(receiver.track);
        audioTracksAdded++;
      }
      if (receiver.track.kind === "video") {
        if (transceiver.mid) {
          receiver.track.mid = transceiver.mid;
          // console.log("🎵 Adding Remote Video Track - ", receiver.track.readyState, "MID:", receiver.track.mid);
          remoteVideoStream.addTrack(receiver.track);
        }
      }
    }
  });

  // Also check receivers directly for any missed tracks
  pc.getReceivers().forEach(function(receiver) {
    if (receiver.track && receiver.track.kind === "audio" && receiver.track.readyState === 'live') {
      // Check if track is already added
      let alreadyAdded = false;
      remoteAudioStream.getAudioTracks().forEach(existingTrack => {
        if (existingTrack.id === receiver.track.id) {
          alreadyAdded = true;
        }
      });

      if (!alreadyAdded) {
        // console.log("🎵 Adding Remote Audio Track from receivers:", receiver.track.id);
        remoteAudioStream.addTrack(receiver.track);
        audioTracksAdded++;
      }
    }
  });

  // Attach Audio - exact Browser-Phone pattern
  if (audioTracksAdded > 0) {
    // console.log(`🎵 Attaching remote audio stream with ${audioTracksAdded} tracks`);
    remoteStream = remoteAudioStream;
    setupRemoteAudio(remoteAudioStream);
    emit('remoteStreamReceived', remoteAudioStream);
  } else {
    console.warn('⚠️ No live remote audio tracks found');

    // Debug: log all receiver tracks
    pc.getReceivers().forEach((receiver, index) => {
      if (receiver.track) {
        // console.log(`Receiver ${index}: kind=${receiver.track.kind}, state=${receiver.track.readyState}, id=${receiver.track.id}`);
      } else {
        // console.log(`Receiver ${index}: No track`);
      }
    });
  }
}

// Setup session description handler - Browser-Phone pattern
function setupSessionDescriptionHandler(session, sdh) {
  try {
    // console.log('🎵 Setting up session description handler (Browser-Phone pattern)');

    if (!sdh || !sdh.peerConnection) {
      console.warn('⚠️ Invalid session description handler');
      return;
    }

    const peerConnection = sdh.peerConnection;

    // CRITICAL: Add local media tracks BEFORE setting up handlers
    if (localStream && localStream.getTracks().length > 0) {
      // console.log('🎵 Adding local stream tracks to peer connection');
      localStream.getTracks().forEach(track => {
        // console.log(`🎵 Adding ${track.kind} track:`, track.label);
        peerConnection.addTrack(track, localStream);
      });
    } else {
      console.warn('⚠️ No local stream available to add to peer connection');
    }

    // Setup ontrack handler - Browser-Phone pattern
    peerConnection.ontrack = function(event) {
      // console.log('🎵 ontrack event fired - calling onTrackAddedEvent');
      onTrackAddedEvent(session);
    };

    // CRITICAL: Also setup a timer to check for remote tracks after SDP negotiation
    // This handles cases where ontrack doesn't fire immediately for outbound calls
    setTimeout(() => {
      // console.log('🎵 Checking for remote tracks after delay');
      const receivers = peerConnection.getReceivers();
      // console.log(`🎵 Found ${receivers.length} receivers`);

      let hasRemoteAudio = false;
      receivers.forEach(receiver => {
        if (receiver.track && receiver.track.kind === 'audio') {
          hasRemoteAudio = true;
          // console.log('🎵 Found remote audio track:', receiver.track.label);
        }
      });

      if (hasRemoteAudio) {
        // console.log('🎵 Triggering delayed onTrackAddedEvent for remote audio');
        onTrackAddedEvent(session);
      } else {
        console.warn('⚠️ No remote audio tracks found after delay');
      }
    }, 1000); // Check after 1 second

    // Handle connection state changes
    peerConnection.onconnectionstatechange = () => {
      // console.log('🔗 Peer connection state:', peerConnection.connectionState);
      if (peerConnection.connectionState === 'connected') {
        // console.log('✅ Peer connection established successfully');

        // Force check for remote tracks when connection is established
        // console.log('🎵 Forcing remote track check on connection established');
        setTimeout(() => onTrackAddedEvent(session), 500);
      }
    };

    peerConnection.oniceconnectionstatechange = () => {
      // console.log('🧊 ICE connection state:', peerConnection.iceConnectionState);
      if (peerConnection.iceConnectionState === 'connected') {
        // console.log('✅ ICE connection established');

        // Additional check when ICE is connected
        // console.log('🎵 Forcing remote track check on ICE connected');
        setTimeout(() => onTrackAddedEvent(session), 500);
      }
    };

    // console.log('✅ Session description handler setup complete (Browser-Phone pattern)');
  } catch (error) {
    // console.error('❌ Error setting up session description handler:', error);
  }
}

// Setup media handlers for established session - Browser-Phone pattern
function setupSessionMediaHandlers(session) {
  try {
    // console.log('🎵 Setting up media handlers for session (Browser-Phone pattern)');

    // Get the session description handler
    const sessionDescriptionHandler = session.sessionDescriptionHandler;
    if (!sessionDescriptionHandler) {
      console.warn('⚠️ No session description handler available');
      return;
    }

    // If not already set up, set it up now
    setupSessionDescriptionHandler(session, sessionDescriptionHandler);

    // console.log('✅ Media handlers setup complete (Browser-Phone pattern)');
  } catch (error) {
    // console.error('❌ Error setting up media handlers:', error);
  }
}

// Setup hold music
function setupHoldMusic() {
  if (!holdMusicAudio) {
    holdMusicAudio = document.createElement('audio');
    holdMusicAudio.id = 'holdMusicAudio';
    holdMusicAudio.loop = true;
    holdMusicAudio.volume = 0.5; // Volume for hold music
    holdMusicAudio.style.display = 'none';
    holdMusicAudio.preload = 'auto';

    // For hold music, let's create an immediate beep pattern instead of file dependency
    // This ensures it works regardless of file paths
    // console.log('🎵 Setting up Web Audio API hold tone directly');
    createHoldTone();

    // Fallback: Create a simple tone using Web Audio API if file not found
    holdMusicAudio.onerror = function() {
      console.warn('🎵 Hold music file not found, creating fallback tone');
      createHoldTone();
    };

    holdMusicAudio.oncanplaythrough = function() {
      // console.log('🎵 Hold music loaded successfully');
    };

    document.body.appendChild(holdMusicAudio);
    // console.log('🎵 Hold music audio element created with source:', holdMusicAudio.src);
  }
}

// Create a pleasant hold tone with multiple frequencies
function createHoldTone() {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();

    // Create multiple oscillators for a more pleasant sound
    const osc1 = audioContext.createOscillator();
    const osc2 = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    const gainNode2 = audioContext.createGain();
    const masterGain = audioContext.createGain();

    // Connect the audio graph
    osc1.connect(gainNode);
    osc2.connect(gainNode2);
    gainNode.connect(masterGain);
    gainNode2.connect(masterGain);
    masterGain.connect(audioContext.destination);

    // Set pleasant frequencies (major third interval)
    osc1.frequency.setValueAtTime(440, audioContext.currentTime); // A4
    osc2.frequency.setValueAtTime(554.37, audioContext.currentTime); // C#5 (major third above A4)

    osc1.type = 'sine';
    osc2.type = 'sine';

    // Set volumes
    gainNode.gain.setValueAtTime(0.05, audioContext.currentTime);
    gainNode2.gain.setValueAtTime(0.03, audioContext.currentTime);
    masterGain.gain.setValueAtTime(0.7, audioContext.currentTime);

    // Create a gentle pulsing effect
    const now = audioContext.currentTime;
    const pulseDuration = 1.0; // 1 second pulse

    // Create gain automation for pulsing
    for (let i = 0; i < 20; i++) { // 20 pulses
      const startTime = now + (i * pulseDuration * 2);
      const endTime = startTime + pulseDuration;

      masterGain.gain.setValueAtTime(0.1, startTime);
      masterGain.gain.linearRampToValueAtTime(0.7, startTime + 0.1);
      masterGain.gain.setValueAtTime(0.7, endTime - 0.1);
      masterGain.gain.linearRampToValueAtTime(0.1, endTime);
    }

    // Store for later cleanup
    holdMusicAudio.audioContext = audioContext;
    holdMusicAudio.oscillators = [osc1, osc2];
    holdMusicAudio.gainNodes = [gainNode, gainNode2, masterGain];

    // console.log('🎵 Pleasant hold tone created with dual frequency');
  } catch (error) {
    // console.error('❌ Failed to create hold tone:', error);
  }
}

// Play hold music
function playHoldMusic() {
  // console.log('🎵 Attempting to play hold music');
  setupHoldMusic();

  if (holdMusicAudio) {
    // If using Web Audio API tone
    if (holdMusicAudio.oscillators && holdMusicAudio.oscillators.length > 0) {
      try {
        holdMusicAudio.oscillators.forEach(osc => osc.start());
        holdMusicAudio.isPlaying = true;
        // console.log('🎵 Hold tone started (Web Audio API)');
        return;
      } catch (error) {
        console.warn('⚠️ Could not start hold tone:', error);
        // Reset and recreate
        createHoldTone();
        if (holdMusicAudio.oscillators) {
          holdMusicAudio.oscillators.forEach(osc => osc.start());
          holdMusicAudio.isPlaying = true;
        }
      }
    } else {
      // Create new tone if not available
      createHoldTone();
      if (holdMusicAudio.oscillators) {
        holdMusicAudio.oscillators.forEach(osc => osc.start());
        holdMusicAudio.isPlaying = true;
        // console.log('🎵 New hold tone created and started');
      }
    }
  } else {
    console.warn('⚠️ Hold music audio element not available');
  }
}

// Stop hold music
function stopHoldMusic() {
  // console.log('🎵 Attempting to stop hold music');

  if (holdMusicAudio && holdMusicAudio.isPlaying) {
    // Stop Web Audio API tone
    if (holdMusicAudio.oscillators && holdMusicAudio.oscillators.length > 0) {
      try {
        holdMusicAudio.oscillators.forEach(osc => {
          try {
            osc.stop();
          } catch (e) {
            // Oscillator might already be stopped
          }
        });

        if (holdMusicAudio.audioContext) {
          holdMusicAudio.audioContext.close();
        }

        holdMusicAudio.oscillators = null;
        holdMusicAudio.gainNodes = null;
        holdMusicAudio.audioContext = null;
        holdMusicAudio.isPlaying = false;

        // console.log('🎵 Hold tone stopped (Web Audio API)');
      } catch (error) {
        console.warn('⚠️ Error stopping hold tone:', error);
      }
    }
  }
}

// Cleanup media streams
function cleanupMedia() {
  // Stop audio health monitoring
  stopAudioHealthCheck();

  if (localStream) {
    localStream.getTracks().forEach(track => {
      track.stop();
    });
    localStream = null;
  }

  const remoteAudio = document.querySelector('#line-remoteAudio');
  if (remoteAudio) {
    remoteAudio.srcObject = null; // Clear the stream
    remoteAudio.remove();
  }

  // Stop hold music
  stopHoldMusic();

  remoteStream = null;
  // console.log('🧹 Media cleanup completed');
}

// Disconnect - clean shutdown
async function disconnect() {
  try {
    // console.log('Disconnecting SIP client...');

    if (userAgent) {
      if (userAgent.registerer) {
        try {
          await userAgent.registerer.unregister();
          // console.log('SIP unregistered');
        } catch (e) {
          console.warn('Error unregistering:', e);
        }
      }

      try {
        await userAgent.stop();
        // console.log('UserAgent stopped');
      } catch (e) {
        console.warn('Error stopping UserAgent:', e);
      }
    }

    userAgent = null;
    sipConfig = null;
    eventCallbacks = {};
    incomingSessions.clear();
    outgoingSessions.clear();
    currentSession = null;
    cleanupMedia();
    // console.log('SIP client disconnected');
  } catch (error) {
    // console.error('Error during disconnect:', error);
    // Force cleanup
    userAgent = null;
    sipConfig = null;
    eventCallbacks = {};
    incomingSessions.clear();
    outgoingSessions.clear();
    currentSession = null;
    cleanupMedia();
  }
}

// Check if registered
function isRegistered() {
  return userAgent && userAgent.isRegistered();
}

// Hangup current call - proper implementation
async function hangup() {
  // console.log('📞 Hangup call requested');

  if (!currentSession) {
    console.warn('⚠️ No current session to hangup');
    return;
  }

  // Store reference to session before it gets set to null by stateChange listener
  const sessionToEnd = currentSession;
  const sessionId = sessionToEnd.id;

  try {
    const sessionState = sessionToEnd.state;
    // console.log('📞 Current session state:', sessionState);

    if (sessionState === window.SIP.SessionState.Initial || sessionState === window.SIP.SessionState.Establishing) {
      // Call hasn't been established yet
      if (sessionToEnd instanceof window.SIP.Inviter) {
        // Outgoing call - cancel it
        await sessionToEnd.cancel();
        // console.log('📞 Outgoing call cancelled');
      } else {
        // Incoming call - reject it
        await sessionToEnd.reject();
        // console.log('📞 Incoming call rejected');
      }
    } else if (sessionState === window.SIP.SessionState.Established) {
      // Call is established - send BYE
      await sessionToEnd.bye();
      // console.log('📞 Call terminated with BYE');
    }

    // Clean up session references using stored ID
    // Note: currentSession may already be null due to stateChange listener
    if (sessionId) {
      incomingSessions.delete(sessionId);
      outgoingSessions.delete(sessionId);
      activeSessions.delete(sessionId);
    }

    // Ensure currentSession is null (may already be null from stateChange)
    if (currentSession === sessionToEnd) {
      currentSession = null;
    }

    if (activeSessions.size === 0) {
      cleanupMedia();
    }

    // Note: callEnded event is emitted by stateChange Terminated listener

  } catch (error) {
    // console.error('❌ Failed to hang up call:', error);
    // Force cleanup even if hangup failed
    if (sessionId) {
      incomingSessions.delete(sessionId);
      outgoingSessions.delete(sessionId);
      activeSessions.delete(sessionId);
    }
    if (currentSession === sessionToEnd) {
      currentSession = null;
    }
    if (activeSessions.size === 0) {
      cleanupMedia();
    }
    throw error;
  }
}

// Hold/unhold functionality - Browser-Phone pattern
async function hold(shouldHold = true) {
  if (!currentSession || currentSession.state !== window.SIP.SessionState.Established) {
    console.warn('⚠️ No active call to hold/unhold');
    return;
  }

  try {
    if (shouldHold) {
      // Check if already on hold
      if (currentSession.isOnHold === true) {
        // console.log("Call is already on hold");
        return;
      }

      // console.log("⏸️ Putting Call on hold (Browser-Phone pattern)");
      currentSession.isOnHold = true;

      // Browser-Phone pattern: set sessionDescriptionHandlerOptions
      let sessionDescriptionHandlerOptions = currentSession.sessionDescriptionHandlerOptionsReInvite || {};
      sessionDescriptionHandlerOptions.hold = true;
      currentSession.sessionDescriptionHandlerOptionsReInvite = sessionDescriptionHandlerOptions;

      const options = {
        requestDelegate: {
          onAccept: function() {
            // console.log('✅ Hold request accepted');

            // Browser-Phone pattern: disable tracks after hold is accepted
            if (currentSession && currentSession.sessionDescriptionHandler && currentSession.sessionDescriptionHandler.peerConnection) {
              const pc = currentSession.sessionDescriptionHandler.peerConnection;

              // Store original track states before disabling for hold
              if (!currentSession.holdTrackStates) {
                currentSession.holdTrackStates = {
                  receivers: [],
                  senders: []
                };
              }

              // Disable inbound streams but remember their state
              pc.getReceivers().forEach(function(RTCRtpReceiver) {
                if (RTCRtpReceiver.track) {
                  currentSession.holdTrackStates.receivers.push({
                    track: RTCRtpReceiver.track,
                    wasEnabled: RTCRtpReceiver.track.enabled
                  });
                  RTCRtpReceiver.track.enabled = false;
                }
              });

              // Disable outbound streams but remember their state
              pc.getSenders().forEach(function(RTCRtpSender) {
                if (RTCRtpSender.track && RTCRtpSender.track.kind === "audio") {
                  // console.log("🎵 Muting Audio Track for hold:", RTCRtpSender.track.label);
                  currentSession.holdTrackStates.senders.push({
                    track: RTCRtpSender.track,
                    wasEnabled: RTCRtpSender.track.enabled
                  });
                  RTCRtpSender.track.enabled = false;
                }
                else if (RTCRtpSender.track && RTCRtpSender.track.kind === "video") {
                  currentSession.holdTrackStates.senders.push({
                    track: RTCRtpSender.track,
                    wasEnabled: RTCRtpSender.track.enabled
                  });
                  RTCRtpSender.track.enabled = false;
                }
              });
            }

            currentSession.isOnHold = true;
            // console.log("⏸️ Call is on hold");

            // Start hold music
            playHoldMusic();

            emit('callHeld');
          },
          onReject: function() {
            currentSession.isOnHold = false;
            console.warn("❌ Failed to put the call on hold");
          }
        }
      };

      // Browser-Phone pattern: send re-INVITE to hold
      await currentSession.invite(options);

    } else {
      // Unhold
      if (currentSession.isOnHold === false) {
        // console.log("Call is already off hold");
        return;
      }

      // console.log("▶️ Taking call off hold (Browser-Phone pattern)");
      currentSession.isOnHold = false;

      // Browser-Phone pattern: set sessionDescriptionHandlerOptions
      let sessionDescriptionHandlerOptions = currentSession.sessionDescriptionHandlerOptionsReInvite || {};
      sessionDescriptionHandlerOptions.hold = false;
      currentSession.sessionDescriptionHandlerOptionsReInvite = sessionDescriptionHandlerOptions;

      const options = {
        requestDelegate: {
          onAccept: function() {
            // console.log('✅ Unhold request accepted');

            // Browser-Phone pattern: restore tracks to their original state after unhold
            if (currentSession && currentSession.sessionDescriptionHandler && currentSession.sessionDescriptionHandler.peerConnection) {
              const pc = currentSession.sessionDescriptionHandler.peerConnection;

              // Restore tracks to their original enabled state from before hold
              if (currentSession.holdTrackStates) {
                // Restore receiver tracks
                currentSession.holdTrackStates.receivers.forEach(function(trackState) {
                  if (trackState.track) {
                    trackState.track.enabled = trackState.wasEnabled;
                    // console.log("🎵 Restored receiver track state:", trackState.track.kind, trackState.wasEnabled);
                  }
                });

                // Restore sender tracks
                currentSession.holdTrackStates.senders.forEach(function(trackState) {
                  if (trackState.track) {
                    trackState.track.enabled = trackState.wasEnabled;
                    // console.log("🎵 Restored sender track state:", trackState.track.kind, trackState.wasEnabled);
                  }
                });

                // Clear the stored states
                currentSession.holdTrackStates = null;
              } else {
                // Fallback: enable all tracks if no stored state
                // console.log("🎵 No stored track states, enabling all tracks");

                pc.getReceivers().forEach(function(RTCRtpReceiver) {
                  if (RTCRtpReceiver.track) RTCRtpReceiver.track.enabled = true;
                });

                pc.getSenders().forEach(function(RTCRtpSender) {
                  if (RTCRtpSender.track && RTCRtpSender.track.kind === "audio") {
                    // console.log("🎵 Unmuting Audio Track:", RTCRtpSender.track.label);
                    RTCRtpSender.track.enabled = true;
                  }
                  else if (RTCRtpSender.track && RTCRtpSender.track.kind === "video") {
                    RTCRtpSender.track.enabled = true;
                  }
                });
              }
            }

            currentSession.isOnHold = false;
            // console.log("▶️ Call is off hold");

            // Stop hold music
            stopHoldMusic();

            emit('callResumed');
          },
          onReject: function() {
            currentSession.isOnHold = true;
            console.warn("❌ Failed to take the call off hold");
          }
        }
      };

      // Browser-Phone pattern: send re-INVITE to unhold
      await currentSession.invite(options);
    }
  } catch (error) {
    // console.error('❌ Error attempting to hold/unhold call:', error);
    if (shouldHold) {
      currentSession.isOnHold = false;
    } else {
      currentSession.isOnHold = true;
    }
    throw error;
  }
}

// Mute/unmute functionality - Browser-Phone pattern
async function mute(shouldMute = true) {
  if (!currentSession || currentSession.state !== window.SIP.SessionState.Established) {
    console.warn('⚠️ No active call to mute/unmute');
    return;
  }

  try {
    // console.log(shouldMute ? '🔇 Muting audio' : '🔊 Unmuting audio');

    // Get peer connection - Browser-Phone pattern
    const pc = currentSession.sessionDescriptionHandler?.peerConnection;
    if (!pc) {
      console.warn('⚠️ No peer connection available for mute');
      return;
    }

    // Browser-Phone pattern: Mute/unmute local audio tracks via RTP senders
    pc.getSenders().forEach(function(sender) {
      if (sender.track && sender.track.kind === 'audio') {
        // console.log(`🎵 ${shouldMute ? 'Muting' : 'Unmuting'} audio track:`, sender.track.label);
        sender.track.enabled = !shouldMute;
      }
    });

    // Also handle local stream tracks if available
    if (localStream) {
      const audioTracks = localStream.getAudioTracks();
      audioTracks.forEach(track => {
        // console.log(`🎵 ${shouldMute ? 'Muting' : 'Unmuting'} local stream track:`, track.label);
        track.enabled = !shouldMute;
      });
    }

    // Set mute state on session for tracking
    currentSession.isMuted = shouldMute;

    // console.log(shouldMute ? '🔇 Audio muted successfully' : '🔊 Audio unmuted successfully');
    emit(shouldMute ? 'callMuted' : 'callUnmuted');

  } catch (error) {
    // console.error('❌ Failed to mute/unmute call:', error);
    throw error;
  }
}

// Check if currently muted
function isMuted() {
  if (!currentSession) return false;
  return currentSession.isMuted === true;
}

// Toggle mute state
async function toggleMute() {
  const currentlyMuted = isMuted();
  await mute(!currentlyMuted);
  return !currentlyMuted;
}

// Answer incoming call - proper implementation with media handling
async function answer(callId) {
  // console.log('📞 Answer call requested for:', callId);

  // Find the stored session
  const session = incomingSessions.get(callId);
  if (!session) {
    // console.error('No session found for callId:', callId);
    throw new Error('Session not found');
  }

  try {
    // console.log('📞 Answering SIP call - getting user media first');

    // CRITICAL: Get user media before answering so tracks are available
    await getUserMedia();

    const options = {
      sessionDescriptionHandlerOptions: {
        constraints: {
          audio: true,
          video: false
        }
      }
    };

    // Accept the session - media handlers should already be set up via delegate
    await session.accept(options);
    // console.log('✅ Call answered successfully');

    // Set as current session - media should already be setup
    currentSession = session;

    emit('callAnswered', callId);

    return session;
  } catch (error) {
    // console.error('❌ Failed to answer call:', error);
    incomingSessions.delete(callId);
    cleanupMedia();
    emit('callFailed', error);
    throw error;
  }
}

// Force audio playback - call this on user interaction to handle autoplay restrictions
function enableAudioPlayback() {
  const remoteAudio = document.querySelector('#line-remoteAudio');
  if (remoteAudio && remoteAudio.paused) {
    remoteAudio.play().then(() => {
      // console.log('✅ Remote audio enabled after user interaction');
    }).catch(error => {
      console.warn('⚠️ Failed to enable remote audio:', error);
    });
  }
}

// Force refresh remote audio - for testing/debugging
function refreshRemoteAudio() {
  if (currentSession && currentSession.sessionDescriptionHandler?.peerConnection) {
    // console.log('🎵 Manually refreshing remote audio');
    onTrackAddedEvent(currentSession);
  } else {
    console.warn('⚠️ No active session to refresh audio for');
  }
}

// Add consultation call - creates a second call while holding the first
async function addCall(numberToCall) {
  if (!userAgent || !userAgent.isRegistered()) {
    throw new Error('SIP extension not registered');
  }

  // console.log('📞 Adding consultation call to:', numberToCall);
  return await makeCall(numberToCall, true); // true indicates consultation call
}

// Transfer call - attended transfer between two calls (Browser-Phone pattern)
async function transferCall(sessionId1, sessionId2) {
  // console.log('📞 Attempting attended transfer between:', sessionId1, sessionId2);

  const session1 = activeSessions.get(sessionId1);
  const session2 = activeSessions.get(sessionId2);

  if (!session1 || !session2) {
    throw new Error('One or both sessions not found for transfer');
  }

  try {
    // For SIP.js, we need to use REFER method for attended transfer
    // First, make sure both calls are established
    if (session1.state !== window.SIP.SessionState.Established ||
        session2.state !== window.SIP.SessionState.Established) {
      throw new Error('Both calls must be established before transfer');
    }

    // console.log('📞 Performing attended transfer via REFER (Browser-Phone pattern)');

    // Track transfer in session data (Browser-Phone pattern)
    if (!session1.data) session1.data = {};
    if (!session1.data.transfer) session1.data.transfer = [];
    session1.data.transfer.push({
      type: "Attended",
      to: session2.remoteIdentity.uri.toString(),
      transferTime: new Date().toISOString(),
      accept: {
        complete: false,
        disposition: "",
        eventTime: ""
      }
    });

    const transferId = session1.data.transfer.length - 1;

    // Create transfer options exactly like Browser-Phone
    const transferOptions = {
      requestDelegate: {
        onAccept: function(sip) {
          // console.log("Attended transfer Accepted");

          // Update transfer tracking
          session1.data.transfer[transferId].accept.complete = true;
          session1.data.transfer[transferId].accept.disposition = sip.message.reasonPhrase;
          session1.data.transfer[transferId].accept.eventTime = new Date().toISOString();

          // console.log('✅ Attended Transfer Complete (Accepted)');

          // Terminate both original sessions after successful transfer
          setTimeout(() => {
            session1.bye().catch(error => {
              console.warn("Could not BYE session1 after attended transfer:", error);
            });
            session2.bye().catch(error => {
              console.warn("Could not BYE session2 after attended transfer:", error);
            });
          }, 250);

          emit('transferAccepted', { sessionId1, sessionId2 });
        },
        onReject: function(sip) {
          console.warn("Attended transfer rejected:", sip);

          // Update transfer tracking
          session1.data.transfer[transferId].accept.complete = false;
          session1.data.transfer[transferId].accept.disposition = sip.message.reasonPhrase;
          session1.data.transfer[transferId].accept.eventTime = new Date().toISOString();

          emit('transferRejected', { sessionId1, sessionId2, reason: sip.message.reasonPhrase });
        }
      }
    };

    // For attended transfer, we refer session1 to session2's remote party
    // This is the Browser-Phone pattern: session.refer(newSession, transferOptions)
    const referRequest = session1.refer(session2, transferOptions);

    // console.log('📞 Attended transfer REFER sent successfully');
    emit('transferInitiated', { sessionId1, sessionId2 });

    return true;

  } catch (error) {
    // console.error('❌ Failed to transfer calls:', error);
    throw error;
  }
}

// Blind transfer - transfer call to a specific number (Browser-Phone pattern)
async function blindTransfer(sessionId, transferNumber) {
  // console.log('📞 Attempting blind transfer of session:', sessionId, 'to:', transferNumber);

  const session = activeSessions.get(sessionId) ||
                 incomingSessions.get(sessionId) ||
                 outgoingSessions.get(sessionId);

  if (!session) {
    throw new Error('Session not found for transfer');
  }

  try {
    // Make sure the call is established
    if (session.state !== window.SIP.SessionState.Established) {
      throw new Error('Call must be established before transfer');
    }

    // console.log('📞 Performing blind transfer via REFER (Browser-Phone pattern)');

    // Check if sipConfig exists for domain logic
    if (!sipConfig || !sipConfig.domain) {
      throw new Error('SIP configuration not available');
    }

    // Use the same domain logic as making calls
    const domainParts = sipConfig.domain.split(':');
    const SipDomain = domainParts[0];

    // Create the transfer URI as a SIP URI object (CRITICAL: Browser-Phone uses URI object, not string)
    const transferSipUri = `sip:${transferNumber.replace(/#/g, '%23')}@${SipDomain}`;
    const referTo = window.SIP.UserAgent.makeURI(transferSipUri);

    if (!referTo) {
      throw new Error('Failed to create transfer URI');
    }

    // console.log('📞 Transfer URI created:', transferSipUri);

    // Track transfer in session data (Browser-Phone pattern)
    if (!session.data) session.data = {};
    if (!session.data.transfer) session.data.transfer = [];
    session.data.transfer.push({
      type: "Blind",
      to: transferNumber,
      transferTime: new Date().toISOString(),
      accept: {
        complete: false,
        disposition: "",
        eventTime: ""
      }
    });

    const transferId = session.data.transfer.length - 1;

    // Create transfer options exactly like Browser-Phone
    const transferOptions = {
      requestDelegate: {
        onAccept: function(sip) {
          // console.log("Blind transfer Accepted");

          // Update transfer tracking
          session.data.transfer[transferId].accept.complete = true;
          session.data.transfer[transferId].accept.disposition = sip.message.reasonPhrase;
          session.data.transfer[transferId].accept.eventTime = new Date().toISOString();

          // console.log('✅ Call Blind Transferred (Accepted)');

          // Terminate the original session after successful transfer
          setTimeout(() => {
            session.bye().catch(error => {
              console.warn("Could not BYE after blind transfer:", error);
            });
          }, 250);

          emit('transferAccepted', { sessionId, transferNumber });
        },
        onReject: function(sip) {
          console.warn("Blind transfer rejected:", sip);

          // Update transfer tracking
          session.data.transfer[transferId].accept.complete = false;
          session.data.transfer[transferId].accept.disposition = sip.message.reasonPhrase;
          session.data.transfer[transferId].accept.eventTime = new Date().toISOString();

          emit('transferRejected', { sessionId, transferNumber, reason: sip.message.reasonPhrase });
        }
      }
    };

    // Send REFER exactly like Browser-Phone (URI object, not string)
    const referRequest = session.refer(referTo, transferOptions);

    // console.log('📞 Blind transfer REFER sent successfully');
    emit('transferInitiated', { sessionId, transferNumber });

    return true;

  } catch (error) {
    // console.error('❌ Failed to perform blind transfer:', error);
    throw error;
  }
}

// Hang up specific session
async function hangupSession(sessionId) {
  // console.log('📞 Hangup session requested for:', sessionId);

  const session = activeSessions.get(sessionId) ||
                 incomingSessions.get(sessionId) ||
                 outgoingSessions.get(sessionId);

  if (!session) {
    console.warn('⚠️ No session found for ID:', sessionId);
    return;
  }

  try {
    const sessionState = session.state;
    // console.log('📞 Session state for hangup:', sessionState);

    if (sessionState === window.SIP.SessionState.Initial ||
        sessionState === window.SIP.SessionState.Establishing) {
      // Call hasn't been established yet
      if (session instanceof window.SIP.Inviter) {
        // Outgoing call - cancel it
        await session.cancel();
        // console.log('📞 Outgoing call cancelled');
      } else {
        // Incoming call - reject it
        await session.reject();
        // console.log('📞 Incoming call rejected');
      }
    } else if (sessionState === window.SIP.SessionState.Established) {
      // Call is established - send BYE
      await session.bye();
      // console.log('📞 Call terminated with BYE');
    }

    // Clean up session references
    activeSessions.delete(sessionId);
    incomingSessions.delete(sessionId);
    outgoingSessions.delete(sessionId);

    // If this was the current session, switch to another or clear
    if (currentSession === session) {
      const remainingSessions = Array.from(activeSessions.values());
      currentSession = remainingSessions.length > 0 ? remainingSessions[0] : null;

      if (currentSession && currentSession.isOnHold) {
        // Resume the remaining call
        hold(false).catch(err => console.warn('Failed to resume remaining call:', err));
      }
    }

    if (activeSessions.size === 0) {
      cleanupMedia();
    }

    // Note: callEnded event is emitted by stateChange Terminated listener

  } catch (error) {
    // console.error('❌ Failed to hang up session:', error);
    // Force cleanup even if hangup failed
    activeSessions.delete(sessionId);
    incomingSessions.delete(sessionId);
    outgoingSessions.delete(sessionId);

    if (currentSession === session) {
      const remainingSessions = Array.from(activeSessions.values());
      currentSession = remainingSessions.length > 0 ? remainingSessions[0] : null;
    }

    if (activeSessions.size === 0) {
      cleanupMedia();
    }

    // Note: callEnded event is emitted by stateChange Terminated listener
    throw error;
  }
}

// Switch active session (for multi-call management)
async function switchToSession(sessionId) {
  // console.log('📞 Switching to session:', sessionId);

  const session = activeSessions.get(sessionId);
  if (!session) {
    throw new Error('Session not found');
  }

  if (currentSession === session) {
    // console.log('📞 Already on requested session');
    return;
  }

  try {
    // Put current session on hold if it exists and is not already on hold
    if (currentSession && !currentSession.isOnHold) {
      // console.log('📞 Putting current session on hold');
      // Use a specific session-based hold method
      await holdSession(currentSession, true);
    }

    // Resume the target session if it's on hold
    if (session.isOnHold) {
      // console.log('📞 Resuming target session');
      await holdSession(session, false);
    }

    currentSession = session;
    // console.log('📞 Switched to session successfully');

  } catch (error) {
    // console.error('❌ Failed to switch sessions:', error);
    throw error;
  }
}

// Hold/unhold specific session
async function holdSession(session, shouldHold = true) {
  if (!session || session.state !== window.SIP.SessionState.Established) {
    console.warn('⚠️ No valid session to hold/unhold');
    return;
  }

  try {
    if (shouldHold) {
      // Check if already on hold
      if (session.isOnHold === true) {
        // console.log("Session is already on hold");
        return;
      }

      // console.log("⏸️ Putting specific session on hold");
      session.isOnHold = true;

      // Set sessionDescriptionHandlerOptions for this specific session
      let sessionDescriptionHandlerOptions = session.sessionDescriptionHandlerOptionsReInvite || {};
      sessionDescriptionHandlerOptions.hold = true;
      session.sessionDescriptionHandlerOptionsReInvite = sessionDescriptionHandlerOptions;

      const options = {
        requestDelegate: {
          onAccept: function() {
            // console.log('✅ Hold request accepted for session');

            // Disable tracks for this session
            if (session && session.sessionDescriptionHandler && session.sessionDescriptionHandler.peerConnection) {
              const pc = session.sessionDescriptionHandler.peerConnection;

              // Store original track states
              if (!session.holdTrackStates) {
                session.holdTrackStates = {
                  receivers: [],
                  senders: []
                };
              }

              // Disable tracks and store states
              pc.getReceivers().forEach(function(RTCRtpReceiver) {
                if (RTCRtpReceiver.track) {
                  session.holdTrackStates.receivers.push({
                    track: RTCRtpReceiver.track,
                    wasEnabled: RTCRtpReceiver.track.enabled
                  });
                  RTCRtpReceiver.track.enabled = false;
                }
              });

              pc.getSenders().forEach(function(RTCRtpSender) {
                if (RTCRtpSender.track) {
                  session.holdTrackStates.senders.push({
                    track: RTCRtpSender.track,
                    wasEnabled: RTCRtpSender.track.enabled
                  });
                  RTCRtpSender.track.enabled = false;
                }
              });
            }

            session.isOnHold = true;
            // console.log("⏸️ Session is on hold");

            // Only play hold music if this session is the current session
            if (session === currentSession) {
              playHoldMusic();
            }

            emit('callHeld', { sessionId: session.id });
          },
          onReject: function() {
            session.isOnHold = false;
            console.warn("❌ Failed to put the session on hold");
          }
        }
      };

      await session.invite(options);

    } else {
      // Unhold
      if (session.isOnHold === false) {
        // console.log("Session is already off hold");
        return;
      }

      // console.log("▶️ Taking session off hold");
      session.isOnHold = false;

      let sessionDescriptionHandlerOptions = session.sessionDescriptionHandlerOptionsReInvite || {};
      sessionDescriptionHandlerOptions.hold = false;
      session.sessionDescriptionHandlerOptionsReInvite = sessionDescriptionHandlerOptions;

      const options = {
        requestDelegate: {
          onAccept: function() {
            // console.log('✅ Unhold request accepted for session');

            // Restore tracks for this session
            if (session && session.sessionDescriptionHandler && session.sessionDescriptionHandler.peerConnection) {
              const pc = session.sessionDescriptionHandler.peerConnection;

              if (session.holdTrackStates) {
                // Restore tracks to original state
                session.holdTrackStates.receivers.forEach(function(trackState) {
                  if (trackState.track) {
                    trackState.track.enabled = trackState.wasEnabled;
                  }
                });

                session.holdTrackStates.senders.forEach(function(trackState) {
                  if (trackState.track) {
                    trackState.track.enabled = trackState.wasEnabled;
                  }
                });

                session.holdTrackStates = null;
              } else {
                // Fallback: enable all tracks
                pc.getReceivers().forEach(function(RTCRtpReceiver) {
                  if (RTCRtpReceiver.track) RTCRtpReceiver.track.enabled = true;
                });

                pc.getSenders().forEach(function(RTCRtpSender) {
                  if (RTCRtpSender.track) RTCRtpSender.track.enabled = true;
                });
              }
            }

            session.isOnHold = false;
            // console.log("▶️ Session is off hold");

            // Stop hold music if this session is the current session
            if (session === currentSession) {
              stopHoldMusic();
            }

            emit('callResumed', { sessionId: session.id });
          },
          onReject: function() {
            session.isOnHold = true;
            console.warn("❌ Failed to take the session off hold");
          }
        }
      };

      await session.invite(options);
    }
  } catch (error) {
    // console.error('❌ Error attempting to hold/unhold session:', error);
    throw error;
  }
}

// Conference functionality - Browser-Phone pattern
async function createConference() {
  // console.log('📞 Creating conference with active sessions');

  const sessionList = Array.from(activeSessions.values());

  if (sessionList.length < 2) {
    throw new Error('Need at least 2 active sessions to create a conference');
  }

  try {
    // console.log('📞 Setting up conference with', sessionList.length, 'sessions');

    // Browser-Phone pattern: Resume all sessions to enable audio mixing
    for (const session of sessionList) {
      if (session.isOnHold) {
        // console.log('📞 Resuming session for conference:', session.id);
        await holdSession(session, false);
      }

      // Enable all audio tracks for the session
      if (session.sessionDescriptionHandler?.peerConnection) {
        const pc = session.sessionDescriptionHandler.peerConnection;

        // Enable all receiver tracks (incoming audio)
        pc.getReceivers().forEach(function(receiver) {
          if (receiver.track && receiver.track.kind === 'audio') {
            receiver.track.enabled = true;
            // console.log('📞 Conference: Enabled incoming audio track for session:', session.id);
          }
        });

        // Enable all sender tracks (outgoing audio)
        pc.getSenders().forEach(function(sender) {
          if (sender.track && sender.track.kind === 'audio') {
            sender.track.enabled = true;
            // console.log('📞 Conference: Enabled outgoing audio track for session:', session.id);
          }
        });
      }

      // Mark session as in conference
      session.isInConference = true;
    }

    // console.log('✅ Conference created successfully with', sessionList.length, 'participants');
    emit('conferenceCreated', { sessionCount: sessionList.length });

    return true;

  } catch (error) {
    // console.error('❌ Failed to create conference:', error);
    throw error;
  }
}

// Add a participant to existing conference
async function addToConference(sessionId) {
  // console.log('📞 Adding session to conference:', sessionId);

  const session = activeSessions.get(sessionId);
  if (!session) {
    throw new Error('Session not found');
  }

  if (session.state !== window.SIP.SessionState.Established) {
    throw new Error('Session must be established before adding to conference');
  }

  try {
    // Resume if on hold
    if (session.isOnHold) {
      await holdSession(session, false);
    }

    // Enable audio tracks
    if (session.sessionDescriptionHandler?.peerConnection) {
      const pc = session.sessionDescriptionHandler.peerConnection;

      pc.getReceivers().forEach(function(receiver) {
        if (receiver.track && receiver.track.kind === 'audio') {
          receiver.track.enabled = true;
        }
      });

      pc.getSenders().forEach(function(sender) {
        if (sender.track && sender.track.kind === 'audio') {
          sender.track.enabled = true;
        }
      });
    }

    session.isInConference = true;
    // console.log('✅ Session added to conference:', sessionId);
    emit('conferenceParticipantAdded', { sessionId });

    return true;

  } catch (error) {
    // console.error('❌ Failed to add session to conference:', error);
    throw error;
  }
}

// Remove a participant from conference
async function removeFromConference(sessionId) {
  // console.log('📞 Removing session from conference:', sessionId);

  const session = activeSessions.get(sessionId);
  if (!session) {
    throw new Error('Session not found');
  }

  try {
    // Put the session on hold to remove from conference
    await holdSession(session, true);

    session.isInConference = false;
    // console.log('✅ Session removed from conference:', sessionId);
    emit('conferenceParticipantRemoved', { sessionId });

    return true;

  } catch (error) {
    // console.error('❌ Failed to remove session from conference:', error);
    throw error;
  }
}

// End conference (remove conference flags but keep calls active)
async function endConference() {
  // console.log('📞 Ending conference (keeping calls active)');

  const sessionList = Array.from(activeSessions.values());
  const conferenceParticipants = sessionList.filter(session => session.isInConference);

  try {
    for (const session of conferenceParticipants) {
      // Remove conference flag but DON'T put on hold - keep calls active
      session.isInConference = false;
      // console.log('📞 Removed conference flag from session:', session.id);
    }

    // console.log('✅ Conference ended, calls remain active individually');
    emit('conferenceEnded');

    return true;

  } catch (error) {
    // console.error('❌ Failed to end conference:', error);
    throw error;
  }
}

// Get all active sessions
function getActiveSessions() {
  return Array.from(activeSessions.entries()).map(([id, session]) => ({
    id,
    session,
    isOnHold: session.isOnHold || false,
    isMuted: session.isMuted || false,
    isInConference: session.isInConference || false,
    state: session.state
  }));
}

// Public API - enhanced with multi-call and transfer functionality
const BrowserPhoneSIP = {
  connect: CreateUserAgent,
  disconnect: disconnect,
  call: makeCall,
  addCall: addCall,
  hangup: hangup,
  hangupSession: hangupSession,
  answer: answer,
  hold: hold,
  holdSession: holdSession,
  mute: mute,
  isMuted: isMuted,
  toggleMute: toggleMute,
  transferCall: transferCall,
  blindTransfer: blindTransfer,
  createConference: createConference,
  addToConference: addToConference,
  removeFromConference: removeFromConference,
  endConference: endConference,
  switchToSession: switchToSession,
  getActiveSessions: getActiveSessions,
  isRegistered: isRegistered,
  enableAudioPlayback: enableAudioPlayback,
  refreshRemoteAudio: refreshRemoteAudio,  // For manual audio refresh
  playHoldMusic: playHoldMusic,  // For testing purposes
  stopHoldMusic: stopHoldMusic,  // For testing purposes
  on: on
};

export default BrowserPhoneSIP;